/* product-gallery.js - the PDP gallery, with no library behind it.

   The viewer is a real scroll-snap track and the thumbnails are real buttons,
   so with this file blocked every media item is still reachable by swipe, by
   scroll and by keyboard. Everything below is an enhancement laid on that
   floor.

   NO ZOOM. The magnifier and the <dialog> lightbox it opened were deleted, by
   the founder's instruction, and nothing replaced them.

   The two arrows are the only control this file adds, and they add NOTHING to
   the model: an arrow click is go(index +/- 1), the exact call the ArrowRight
   key already made. So the arrow, the key, the thumbnail and the swipe are four
   doors into one scroll container, and a shopper who swipes past the arrow's
   last known position leaves it correct, because the arrow never held a
   position of its own to be wrong about.

   The arrows are drawn at every width, and on a phone they sit ON the track.
   That is the one place this theme puts a control in the path of a gesture, and
   forward() below is the whole answer to it: the disc hands every horizontal
   pixel back to the track's own scrollLeft instead of swallowing it. It is a
   fifth door into the same room, not a second room.

   init() is idempotent and re-runs on shopify:section:load, because the theme
   editor replaces a section's innerHTML and does NOT re-execute <script src>:
   without it every merchant edit would leave these listeners on detached nodes.

   The active slide is DERIVED from an IntersectionObserver on the track rather
   than written by the click handler, so a swipe and a click agree by
   construction and there is no second source of truth to drift. */
(function () {
  'use strict';

  var PP = window.PP;
  if (window.ppGalleryLoaded) return;
  window.ppGalleryLoaded = true;

  var ROOT = '[data-gallery]';
  /* A Map, NOT a WeakMap, and that is the whole point. The theme editor fires
     shopify:section:load on a re-render and fires NO unload (unload is for
     delete and hide), so a teardown reachable only through the old node is a
     teardown that never runs: its document level listener survives, closed over
     a detached tree, once per merchant keystroke. An iterable registry can be
     swept for nodes that have left the document. */
  var live = new Map();

  function sweep() {
    live.forEach(function (stop, node) {
      if (node.isConnected) return;
      live.delete(node);
      stop();
    });
  }

  /* A selector rebuilt from platform data (a media id) is escaped AND wrapped,
     so an unexpected value costs the scroll target and nothing else. */
  function esc(value) {
    if (window.CSS && CSS.escape) return CSS.escape(String(value));
    return String(value).replace(/["\\]/g, '\\$&');
  }

  function destroy(root) {
    var stop = live.get(root);
    if (!stop) return;
    live.delete(root);
    stop();
  }

  function setup(root) {
    destroy(root);

    var track = root.querySelector('[data-gallery-track]');
    if (!track) return;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    var prevBtn = root.querySelector('[data-gallery-prev]');
    var nextBtn = root.querySelector('[data-gallery-next]');
    var status = root.querySelector('[data-gallery-status]');
    var statusLabel = status ? status.getAttribute('data-label-status') || '' : '';
    var host = root.closest('[data-section-id]');
    var sectionId = host ? host.getAttribute('data-section-id') : null;
    var off = [];
    var index = -1;

    function on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      off.push(function () {
        target.removeEventListener(type, handler, options);
      });
    }

    /* The ends STOP. go() has always clamped (an undefined slide returns early),
       so a wrapping arrow would have been a second and contradicting answer to
       "what follows the last image", and a click on a spent arrow is already a
       no-op without a line of its own.

       aria-disabled, NOT the disabled property, and the choice is load bearing
       twice. A disabled button leaves the tab order, so a keyboard shopper
       standing on the arrow that switches off is dropped on <body> and has to
       restart the tab cycle; W3C APG recommends aria-disabled for carousel
       controls for exactly that. And Chromium dispatches NO pointer event on a
       disabled control, which would silently kill forward() on the spent arrow,
       at image 1, on the left edge, which is where a thumb starts. The earlier
       cut of this file used `disabled` and needed a focus migration hack to
       survive its own first reason; both the hack and the second defect go away
       with the attribute. */
    function arm(at) {
      if (!prevBtn || !nextBtn) return;
      prevBtn.setAttribute('aria-disabled', String(at <= 0));
      nextBtn.setAttribute('aria-disabled', String(at >= slides.length - 1));
    }

    /* aria-current on a thumbnail is a property a screen reader reports when
       asked. It is not an event, so a swipe would move the picture in silence.
       This one polite line is what speaks. */
    function announce(at) {
      if (!status || !statusLabel) return;
      status.textContent = statusLabel
        .replace('{index}', String(at + 1))
        .replace('{count}', String(slides.length));
    }

    function mark(next) {
      if (next === index || next < 0 || next >= slides.length) return;
      index = next;
      thumbs.forEach(function (thumb, i) {
        if (i === next) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      arm(next);
      announce(next);
    }

    /* THE SWEEP IS THE ONLY MOTION THIS FILE OWNS, AND THE GLOBAL CAP CANNOT
       REACH IT. assets/base.css forces `scroll-behavior: auto !important` under
       prefers-reduced-motion, but CSSOM View lets an explicit `behavior` in
       ScrollToOptions win over the computed property, so the cap stops at the
       stylesheet and a scripted scroll walks straight past it. Measured in
       Chromium with that exact cap applied and the media query matching:
       `behavior: 'smooth'` leaves scrollLeft at 0 in the calling task and at 14
       of 800 one frame later, while `'auto'` is already on the target in the
       same task. A shopper who asked for no motion was therefore still watching
       the largest image on the page sweep sideways, on a thumbnail click, on an
       arrow key and on every variant change.

       Read per gesture rather than cached at load, which is the position
       assets/drawer-swipe.js already takes for the same reason: the setting can
       change mid session, and a MediaQueryList captured here would answer for
       the session it was born in. `go(start, 'auto')` below keeps passing its
       own value, because a gallery that opens on a non first image has nothing
       to animate from. */
    function ease() {
      return PP && PP.reducedMotion && PP.reducedMotion() ? 'auto' : 'smooth';
    }

    function go(next, behavior) {
      var slide = slides[next];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: behavior || ease() });
      mark(next);
    }

    /* ---- navigation ------------------------------------------------------- */

    /* THE ARROW HANDS THE DRAG BACK.
       The disc is a SIBLING of the track, not a descendant, because a scroll
       container's absolutely positioned child scrolls away with the content.
       The price is that a finger landing on the disc has no horizontally
       scrollable ancestor: the browser has nothing to start, and the swipe dies
       on 44px at each edge. On a 375 phone that is a tenth of the image, at the
       exact edge a thumb reaches for.

       So the control forwards rather than swallows. `touch-action: pan-y` in
       the snippet leaves the PAGE's vertical scroll to the browser and claims
       the horizontal axis; every horizontal pixel is written into
       track.scrollLeft, the same property the native swipe moves. The
       IntersectionObserver stays the one and only thing that derives the index,
       so this adds motion, never state.

       Snap is suspended for the duration and the landing is handed to go(), the
       file's single stopping path, so a drag begun on the disc finishes exactly
       where a click would have. Leaving mandatory snap armed would instead have
       the container re-snap on every assignment and the image would jump
       between crans under the finger rather than follow it.

       WHAT IS LOST, stated rather than hidden: the browser does not lend its
       fling to a scroll it did not start, so a drag begun ON the disc travels
       as far as the finger and then snaps, with no momentum. A drag begun
       anywhere else on the image is untouched and keeps it. That asymmetry is
       the price of a control sitting over a scroller, and it is cheaper than a
       dead edge.

       Pointer capture is not optional: without it the finger leaves the 44px
       box within a few pixels and pointermove stops arriving. */
    function forward(button) {
      var origin = 0;
      var from = 0;
      var dragging = false;
      var moved = false;

      on(button, 'pointerdown', function (event) {
        if (event.pointerType === 'mouse') return;
        dragging = true;
        moved = false;
        from = event.clientX;
        origin = track.scrollLeft;
        try {
          button.setPointerCapture(event.pointerId);
        } catch (error) {
          /* No capture available: the drag simply will not be forwarded. */
        }
      });

      on(button, 'pointermove', function (event) {
        if (!dragging) return;
        /* 8px of slop, so a tap with a trembling thumb stays a tap. The origin
           is REBASED at the moment the threshold falls rather than kept from
           pointerdown: reading the raw delta there would hand the track those 8
           pixels all at once and the image would jump before it followed. The
           cost is that the gesture gives up its first 8px of travel, which is
           what every drag threshold costs and what nobody sees. */
        if (!moved) {
          if (Math.abs(event.clientX - from) < 8) return;
          moved = true;
          from = event.clientX;
          origin = track.scrollLeft;
          track.style.scrollSnapType = 'none';
          return;
        }
        track.scrollLeft = origin - (event.clientX - from);
      });

      function release(event) {
        if (!dragging) return;
        dragging = false;
        try {
          button.releasePointerCapture(event.pointerId);
        } catch (error) {
          /* Already released, or never captured. */
        }
        if (!moved) return;
        track.style.scrollSnapType = '';
        /* The nearest slide by the SAME offset arithmetic go() uses, so the two
           cannot drift apart on a padding change. */
        var target = 0;
        var best = Infinity;
        slides.forEach(function (slide, i) {
          var gap = Math.abs(slide.offsetLeft - track.offsetLeft - track.scrollLeft);
          if (gap < best) {
            best = gap;
            target = i;
          }
        });
        go(target);
      }

      on(button, 'pointerup', release);
      on(button, 'pointercancel', release);

      /* Read once and cleared, so a drag that ends off the button (pointer
         capture retargets the move, never the click) cannot leave the flag set
         and swallow the next real tap. pointerdown clears it too. */
      return function dragged() {
        var was = moved;
        moved = false;
        return was;
      };
    }

    /* Read `index` at CLICK time, not at wiring time. The shopper may have
       swiped, scrolled, tapped a thumbnail or changed variant since the page
       loaded, and every one of those routes through mark(), so `index` is the
       position the viewer is actually showing rather than the one the arrow
       last put it in. */
    if (prevBtn) {
      var prevDragged = forward(prevBtn);
      on(prevBtn, 'click', function () {
        if (prevDragged()) return;
        go(index - 1);
      });
    }

    if (nextBtn) {
      var nextDragged = forward(nextBtn);
      on(nextBtn, 'click', function () {
        if (nextDragged()) return;
        go(index + 1);
      });
    }

    thumbs.forEach(function (thumb, i) {
      on(thumb, 'click', function () {
        go(i);
      });
    });

    on(track, 'keydown', function (event) {
      var back = event.key === 'ArrowLeft' || event.key === 'ArrowUp';
      var forward = event.key === 'ArrowRight' || event.key === 'ArrowDown';
      if (!back && !forward) return;
      event.preventDefault();
      go(index + (forward ? 1 : -1));
    });

    var watcher = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          mark(slides.indexOf(entry.target));
        });
      },
      { root: track, threshold: 0.55 }
    );
    slides.forEach(function (slide) {
      watcher.observe(slide);
    });
    off.push(function () {
      watcher.disconnect();
    });

    /* ---- the variant tells the gallery which media to show ---------------- */

    on(document, 'pp:variant:change', function (event) {
      var detail = event.detail || {};
      if (sectionId && detail.sectionId && detail.sectionId !== sectionId) return;
      var variant = detail.variant;
      var mediaId = Number(variant && variant.featured_media && variant.featured_media.id);
      if (!isFinite(mediaId)) return;
      var slide = null;
      try {
        slide = root.querySelector('[data-media-id="' + esc(mediaId) + '"]');
      } catch (error) {
        return;
      }
      if (!slide) return;
      var at = slides.indexOf(slide);
      if (at >= 0) go(at);
    });

    live.set(root, function () {
      off.forEach(function (fn) {
        fn();
      });
      off = [];
    });

    var start = parseInt(root.getAttribute('data-gallery-index'), 10) || 0;
    mark(start);
    if (start > 0) go(start, 'auto');
  }

  function init(scope) {
    sweep();
    var node = scope && scope.querySelectorAll ? scope : document;
    if (node.matches && node.matches(ROOT)) setup(node);
    Array.prototype.forEach.call(node.querySelectorAll(ROOT), setup);
  }

  init(document);

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var node = event.target;
    if (node && node.querySelectorAll) Array.prototype.forEach.call(node.querySelectorAll(ROOT), destroy);
    sweep();
  });
})();
